/**
 * Canonical client_contact.type codes and their friendly labels.
 *
 * `client_contact.type` is stored as a varchar holding one of these numeric
 * codes. This is the single source of truth for both the backend (labelling and
 * filtering the Contacts list) and the frontend (filter options), so the mapping
 * doesn't drift between them.
 */
export const CLIENT_CONTACT_TYPES = {
	OWNER: 1,
	PHARMACIST: 2,
	EMPLOYEE: 3,
	BUYER: 4,
	MANAGER: 5,
	SALES_AP: 6,
} as const;

const LABEL_BY_VALUE = new Map<number, string>([
	[CLIENT_CONTACT_TYPES.OWNER, 'Owner'],
	[CLIENT_CONTACT_TYPES.PHARMACIST, 'Pharmacist'],
	[CLIENT_CONTACT_TYPES.EMPLOYEE, 'Employee'],
	[CLIENT_CONTACT_TYPES.BUYER, 'Buyer'],
	[CLIENT_CONTACT_TYPES.MANAGER, 'Manager'],
	[CLIENT_CONTACT_TYPES.SALES_AP, 'Sales/AP'],
]);

/**
 * Resolve a stored `client_contact.type` (numeric code as string or number) to a
 * friendly label. Returns null for empty values, and echoes back an unknown
 * non-empty value unchanged.
 */
export function clientContactTypeLabel(
	type: string | number | null | undefined,
): string | null {
	if (type == null || type === '') {
		return null;
	}

	const numeric = typeof type === 'number' ? type : parseInt(type, 10);

	if (Number.isNaN(numeric)) {
		return typeof type === 'string' ? type : null;
	}

	return LABEL_BY_VALUE.get(numeric) ?? String(type);
}

/**
 * Contact-type options for a filter ({ id, label }), where `id` is the stored
 * `client_contact.type` value.
 */
export const CLIENT_CONTACT_TYPE_OPTIONS: ReadonlyArray<{
	id: string;
	label: string;
}> = Array.from(LABEL_BY_VALUE.entries()).map(([value, label]) => ({
	id: String(value),
	label,
}));
